import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { InventoryOutputDetailModel } from 'src/app/models/inventory/inventory-output-detail.model';
import { InventoryOutputModel } from 'src/app/models/inventory/inventory-output.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { InventoryOutputDetailService } from 'src/app/services/inventory/inventory-output-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-inventory-output-detail',
  templateUrl: './register-inventory-output-detail.component.html',
  styleUrls: ['./register-inventory-output-detail.component.scss'],
  standalone: false
})
export class RegisterInventoryOutputDetailComponent implements OnChanges {

  @Input() inventoryOutputDetailContract: InventoryOutputDetailModel = new InventoryOutputDetailModel();
  @Input() inventoryOutputModel: InventoryOutputModel = new InventoryOutputModel();
  @Input() permissionUpdatable: boolean = false;
  @Input() swAllowsNegativeShopBalances: boolean = false;
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  articleId: number = 0;
  codeNameArticle: string = '';
  codeNameStore: string = '';
  formInventoryOutputDetail: FormGroup = new FormGroup({});
  inventoryOutputDetailId: number = 0;
  stockArticle: number = 0;
  stockArticleInput: number = 0;
  storeId: number = 0;
  subscription: Subscription;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private inventoryOutputDetailService: InventoryOutputDetailService,
    private storeService: StoreService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.inventoryOutputDetailContract);
  }

  async ngOnChanges(changes: SimpleChanges) {
    const inventoryOutputDetailContract = changes["inventoryOutputDetailContract"];
    if (this.inventoryOutputDetailContract.inventoryOutputDetailId === 0 && inventoryOutputDetailContract) {
      this.createNewInventoryOutputDetail();
    } else {
      this.inventoryOutputDetailId = this.inventoryOutputDetailContract.inventoryOutputDetailId;
      await this.getStore(this.inventoryOutputDetailContract.storeId, false);
      await this.getArticle(this.inventoryOutputDetailContract.articleId, false);
      this.validateForm(this.inventoryOutputDetailContract);
    }
  }

  calculateQuantities(quantity: number) {
    if (!this.swAllowsNegativeShopBalances && (quantity > this.stockArticle + this.inventoryOutputDetailContract.quantity)) {
      this.toastHandlerService.showToast('warning', 'La cantidad no puede ser mayor a las existencias.');
      this.formInventoryOutputDetail.controls["quantity"].patchValue(this.inventoryOutputDetailContract.quantity);
      this.stockArticleInput = this.stockArticle;
    } else {
      if (quantity < 0) {
        this.toastHandlerService.showToast('warning', 'La cantidad no puede ser menor a 0.');
        this.formInventoryOutputDetail.controls["quantity"].patchValue(this.inventoryOutputDetailContract.quantity);
        this.stockArticleInput = this.stockArticle;
      } else {
        this.stockArticleInput = this.stockArticle + this.inventoryOutputDetailContract.quantity - quantity;
      }
    }
  }

  clearForm() {
    this.codeNameArticle = '';
    this.inventoryOutputDetailContract = new InventoryOutputDetailModel();
    this.inventoryOutputDetailId = 0;
    this.storeId = 0;
    this.articleId = 0;
    this.validateForm(this.inventoryOutputDetailContract);
    this.isFormView.emit(false);
  }

  createNewInventoryOutputDetail() {
    this.codeNameArticle = '';
    this.codeNameStore = '';
    this.storeId = 0;
    this.articleId = 0;
    this.stockArticleInput = 0;
    this.inventoryOutputDetailId = 0;
    this.validateForm(this.inventoryOutputDetailContract);
  }

  async getArticle(articleId: number | null, reload: boolean) {
    if (articleId !== null && articleId !== 0) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        this.codeNameArticle = `${article.code} - ${article.name}`;
        this.formInventoryOutputDetail.controls["articleId"].patchValue(articleId);
        this.articleId = articleId;
        this.getStocksByArticleIdStoreId();
      } else {
        this.articleId = 0;
        this.formInventoryOutputDetail.controls["articleId"].patchValue(0);
        this.codeNameArticle = "Error al buscar el elemento. Por favor refresque la página.";
        return;
      }
      if (reload) {
        this.getInventoryOutputDetailByKeys();
      }
    } else {
      this.articleId = 0;
      this.formInventoryOutputDetail.controls["articleId"].patchValue(0);
      this.codeNameArticle = "";
      if (reload) {
        const storeId = this.formInventoryOutputDetail.get("storeId")?.value;
        this.inventoryOutputDetailContract = new InventoryOutputDetailModel();
        if (storeId !== 0) {
          this.inventoryOutputDetailContract.storeId = storeId;
          this.validateForm(this.inventoryOutputDetailContract);
        }
      }
    }
  }

  async getInventoryOutputDetailByKeys() {
    if (this.storeId !== 0 && this.articleId !== 0) {
      try {
        this.inventoryOutputDetailContract = await firstValueFrom(
          this.inventoryOutputDetailService.getInventoryOutputDetailByKeys(this.inventoryOutputModel.inventoryOutputId, this.storeId, this.articleId)
        );
        this.inventoryOutputDetailId = this.inventoryOutputDetailContract.inventoryOutputDetailId;
        this.storeId = this.inventoryOutputDetailContract.storeId;
        this.articleId = this.inventoryOutputDetailContract.articleId;
        this.validateForm(this.inventoryOutputDetailContract);
      } catch (error) {
        const modelInventoryOutputDetail = new InventoryOutputDetailModel();
        modelInventoryOutputDetail.storeId = this.storeId;
        modelInventoryOutputDetail.articleId = this.articleId;
        this.inventoryOutputDetailContract = modelInventoryOutputDetail;
        this.validateForm(modelInventoryOutputDetail);
        console.error('Error al buscar el detalle de la salida de inventario por llaves.', error);
      }
    }
  }

  async getStocksByArticleIdStoreId() {
    if (this.storeId != 0 && this.articleId != 0) {
      const rentalStockModel = new RentalStockModel();
      rentalStockModel.articleId = this.articleId;
      rentalStockModel.storeId = this.storeId;
      rentalStockModel.dateMovement = this.inventoryOutputModel.dateOutput;
      try {
        const stock = await firstValueFrom(this.inventoryOutputDetailService.getStocksByArticleIdStoreId(rentalStockModel));
        this.stockArticle = stock;
        this.stockArticleInput = stock;
        this.formInventoryOutputDetail.controls["stock"].patchValue(stock);
      } catch (error) {
        console.error('Error al buscar las existencias por artículo y bodega.', error);
      }
    }
  }

  async getStore(storeId: number | null, reload: boolean) {
    if (storeId !== null && storeId !== 0) {
      this.storeId = storeId;
      const store = await firstValueFrom(this.storeService.getStoreById(storeId));
      if (store) {
        this.codeNameStore = `${store.code} - ${store.name}`;
      } else {
        this.codeNameStore = "";
      }
      if (reload) {
        this.inventoryOutputDetailId = 0;
        this.articleId = 0;
        this.codeNameArticle = "";
        const modelInventoryOutputDetail = new InventoryOutputDetailModel();
        modelInventoryOutputDetail.storeId = this.storeId;
        this.validateForm(modelInventoryOutputDetail);
      }
    } else {
      this.storeId = 0;
      this.codeNameArticle = "";
      this.formInventoryOutputDetail.controls["storeId"].patchValue(0);
    }
  }

  async send() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar salidas de inventario. Comuníquese con el administrador del sistema.');
      return;
    }
    Object.keys(this.formInventoryOutputDetail.controls).forEach(key => {
      const control = this.formInventoryOutputDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInventoryOutputDetail.valid) {
      const modelInventoryOutputDetail = this.formInventoryOutputDetail.value as InventoryOutputDetailModel;
      modelInventoryOutputDetail.inventoryOutputId = this.inventoryOutputModel.inventoryOutputId;
      modelInventoryOutputDetail.internalOrderDetailId = this.inventoryOutputDetailContract.internalOrderDetailId;

      const rentalStockModel = new RentalStockModel();
      rentalStockModel.articleId = modelInventoryOutputDetail.articleId;
      rentalStockModel.storeId = modelInventoryOutputDetail.storeId;
      rentalStockModel.dateMovement = this.inventoryOutputModel.dateOutput;

      //Busco las existencias antes de crear o modificar para mandarlo dentro del modelo:
      try {
        const stocksByArticle = await firstValueFrom(this.inventoryOutputDetailService.getStocksByArticleIdStoreId(rentalStockModel));
        modelInventoryOutputDetail.stock = stocksByArticle;
        if (this.inventoryOutputDetailId == 0) {
          try {
            await firstValueFrom(this.inventoryOutputDetailService.createInventoryOutputDetail(modelInventoryOutputDetail));
            this.toastHandlerService.showToast(
              'success',
              'El detalle de la salida fue creado exitosamente.'
            );
            this.updateListDetail.emit(true);
          } catch (error) {
            console.error('Error al crear el detalle de la salida.', error);
          }
        } else {
          this.inventoryOutputDetailContract.stock = modelInventoryOutputDetail.stock;
          this.inventoryOutputDetailContract.quantity = modelInventoryOutputDetail.quantity;
          try {
            await firstValueFrom(this.inventoryOutputDetailService.updateInventoryOutputDetail(this.inventoryOutputDetailContract));
            this.toastHandlerService.showToast(
              'success',
              'El detalle de la salida fue modificado exitosamente.'
            );
            this.updateListDetail.emit(true);
          } catch (error) {
            this.updateListDetail.emit(true);
            console.error('Error al modificar el detalle de la salida.', error);
          }
        }

        this.inventoryOutputDetailContract = new InventoryOutputDetailModel();
        this.createNewInventoryOutputDetail();
      } catch (error) {
        console.error('Error al obtener las existencias por artículo y bodega.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debe seleccionar una Bodega válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'Debe ingresar una Cantidad igual o mayor a 1.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInventoryOutputDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(inventoryOutputDetail: InventoryOutputDetailModel) {
    this.formInventoryOutputDetail = this.formBuilder.group({
      storeId: [inventoryOutputDetail.storeId, [Validators.required, Validators.min(1)]],
      articleId: [inventoryOutputDetail.articleId, [Validators.required, Validators.min(1)]],
      stock: [{ value: inventoryOutputDetail.stock ?? 0, disabled: true }],
      quantity: [inventoryOutputDetail.quantity, [Validators.required, Validators.min(1)]],
      quantityPending: [{ value: inventoryOutputDetail.quantityPending ?? 0, disabled: true }]
    });
    if (inventoryOutputDetail.storeId !== 0) {
      this.getStore(inventoryOutputDetail.storeId, false);
    }
    if (inventoryOutputDetail.articleId !== 0) {
      this.getArticle(inventoryOutputDetail.articleId, false);
    }
  }

}
